import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Stack, Divider,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, alpha, useTheme, Tooltip,
  Card, CardContent, Grid, Autocomplete, InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Search, Download, FilterList, Person, AccessTime,
  CheckCircle, HourglassEmpty, FolderOpen, PictureAsPdf,
  ExpandMore, ExpandLess, Layers, Assignment,
} from '@mui/icons-material';
import PageHeader from '../../components/headers/Header';
import { getAllTasks, getLocalProjectsData, searchTimesheets } from '../../services/clockwiseAPI';
import { getAllEmployees } from '../../services/HRstaff';
import { DAY_NAMES, formatHours, decodeToken, getDateOfDay } from './clockwiseUtils';
import { format, parseISO, isWithinInterval } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import marabesLogo from '../../assets/images/logos/Marabes.png';

// ─── Helper: load image as base64 ────────────────────────────────────────────
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, flex: 1, minWidth: 160 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(color, 0.1) }}>
            <Icon sx={{ fontSize: 20, color }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── Breakdown Table ──────────────────────────────────────────────────────────
const BreakdownSection = ({ title, icon: Icon, color, rows, labelKey, totalHours }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, overflow: 'hidden', mb: 2 }}>
      <Box
        sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(color, 0.06), cursor: 'pointer' }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon sx={{ fontSize: 18, color }} />
          <Typography fontWeight={700} variant="body2" color={color}>{title}</Typography>
          <Chip label={`${rows.length} entries`} size="small" sx={{ bgcolor: alpha(color, 0.12), color }} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={800} color={color}>{formatHours(totalHours)} total</Typography>
          <IconButton size="small">{expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
        </Stack>
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: alpha(color, 0.04), color: 'text.secondary' }}>{labelKey}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(color, 0.04), color: 'text.secondary' }}>Hours</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(color, 0.04), color: 'text.secondary' }}>% of Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.label || 'Unknown'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={700} color={color}>{formatHours(row.hours)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{ width: 60, height: 6, bgcolor: 'grey.100', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${totalHours > 0 ? (row.hours / totalHours) * 100 : 0}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'center' }}>
                        {totalHours > 0 ? `${((row.hours / totalHours) * 100).toFixed(1)}%` : '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TimesheetOverview() {
  const theme = useTheme();
  const me = useMemo(() => decodeToken(), []);
  const isAdmin = me?.role === 'planning' || me?.role === 'HR' || me?.role === 'MANAGER';

  // ── Master Data ──
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [masterLoaded, setMasterLoaded] = useState(false);

  // ── Filters ──
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [fromDate, setFromDate] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedWeek, setSelectedWeek] = useState('');

  const handleWeekChange = (val) => {
    setSelectedWeek(val);
    if (!val) return;
    try {
      const [y, w] = val.split('-W');
      if (y && w) {
        setFromDate(getDateOfDay(parseInt(y, 10), parseInt(w, 10), 0));
        setToDate(getDateOfDay(parseInt(y, 10), parseInt(w, 10), 6));
      }
    } catch { /* ignore */ }
  };

  /** Convert stored ISO date (yyyy-MM-dd) → display label dd/MM/yyyy */
  const fmtDisplay = (iso) => {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    } catch {
      return iso;
    }
  };

  // ── Results ──
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  // ── Load master data on first open ──
  const loadMasterData = useCallback(async () => {
    if (masterLoaded) return;
    try {
      const [projData, empRes] = await Promise.all([
        getLocalProjectsData().catch(() => ({ projects: [], clients: [], services: [] })),
        getAllEmployees().catch(() => []),
      ]);
      setProjects(projData.projects || []);
      setClients(projData.clients || []);
      setServices(projData.services || []);
      const rawEmps = Array.isArray(empRes?.data) ? empRes.data : Array.isArray(empRes) ? empRes : empRes?.users || [];
      const mappedEmps = rawEmps.map(e => ({
        ...e,
        id: e.employeeId || e.id,
        name: e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim()
      }));
      setEmployees(mappedEmps);
      setMasterLoaded(true);
    } catch (err) {
      console.error('[TimesheetOverview] master data error:', err);
    }
  }, [masterLoaded]);

  React.useEffect(() => { loadMasterData(); }, [loadMasterData]);

  // ── Derived Data for Filters ──
  const departments = useMemo(() => {
    const list = employees.map(e => e.department?.name).filter(Boolean);
    return ['all', ...new Set(list)];
  }, [employees]);

  const offices = useMemo(() => {
    const list = employees.map(e => e.office?.name).filter(Boolean);
    return ['all', ...new Set(list)];
  }, [employees]);

  const filteredEmployeesList = useMemo(() => {
    return employees.filter(e => {
      if (selectedDepartment !== 'all' && e.department?.name !== selectedDepartment) return false;
      if (selectedOffice !== 'all' && e.office?.name !== selectedOffice) return false;
      return true;
    });
  }, [employees, selectedDepartment, selectedOffice]);

  const filteredClientsList = useMemo(() => {
    if (!selectedProject) return clients;
    return clients.filter(c => c.project_id === selectedProject.id);
  }, [clients, selectedProject]);

  const filteredServicesList = useMemo(() => {
    if (selectedClient && selectedClient.Services) {
      return selectedClient.Services.map(s => ({
        id: s.id,
        service: s.service,
        name: s.service,
        department_id: s.department_id,
        type: 'service'
      }));
    }
    if (selectedProject) {
      const svcs = new Map();
      const projClients = clients.filter(c => c.project_id === selectedProject.id);
      projClients.forEach(c => {
        (c.Services || []).forEach(s => {
          if (!svcs.has(s.id)) svcs.set(s.id, { id: s.id, service: s.service, name: s.service, type: 'service' });
        });
      });
      return Array.from(svcs.values());
    }
    return services;
  }, [services, clients, selectedClient, selectedProject]);

  // ── Helper: resolve project name from a task record ──
  const resolveProjectName = useCallback((r) => {
    // Try matching by project_id or pathLevel1
    const match = projects.find(p =>
      String(p.id) === String(r.project_id) ||
      String(p.id) === String(r.pathLevel1) ||
      p.name === r.pathLevel1
    );
    return match?.name || r.pathLevel1 || r.project_id || 'Unknown';
  }, [projects]);

  // ── Helper: resolve client name from a task record ──
  const resolveClientName = useCallback((r) => {
    if (r.client_name) return r.client_name;
    const cl = clients.find(c =>
      String(c.id) === String(r.client_id) ||
      c.name === r.pathLevel2
    );
    return cl?.name || r.pathLevel2 || r.client_name || 'Unknown';
  }, [clients]);

  // ── Helper: resolve service name from a task record ──
  const resolveServiceName = useCallback((r) => {
    // Try service_name, then pathLevel3, then pathLevel2
    if (r.service_name) return r.service_name;
    const svc = services.find(s =>
      String(s.id) === String(r.service_id) ||
      s.name === r.pathLevel3 ||
      s.name === r.pathLevel2
    );
    return svc?.name || r.pathLevel3 || r.pathLevel2 || r.client_name || r.marabes_name || r.external_name || 'Unknown';
  }, [services]);

  // ── Search ──
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build API filters if possible (helps performance and accuracy)
      const apiFilters = {
        from: fromDate,
        to: toDate,
        user_id: selectedEmployees.length === 1 ? selectedEmployees[0].id : undefined,
      };

      const data = await searchTimesheets(apiFilters);
      const rawData = Array.isArray(data) ? data : (data?.clockwise || []);

      const startDate = parseISO(fromDate);
      const endDate = parseISO(toDate);

      // Normalize: data may be nested or flat
      const flat = rawData.flatMap((record) => {
        const baseUserId = record.user_id || record.user?.id;
        const baseEmpName = employees.find(e => e.id === baseUserId)?.name || record.user?.name || 'Unknown';
        const year = record.year || record.yearNumber;
        const week = record.week || record.weekNumber;

        if (record.tasks && Array.isArray(record.tasks)) {
          return record.tasks.map((t) => {
            let taskDate = t.date;
            if (!taskDate && year && week && t.day !== undefined) {
              try { taskDate = getDateOfDay(parseInt(year), parseInt(week), parseInt(t.day)); } catch { }
            }
            return {
              ...t,
              user_id: t.user_id || baseUserId,
              employeeName: t.user?.name || baseEmpName,
              weekNum: week,
              year: year,
              date: taskDate
            };
          });
        }

        // Flat record
        let recDate = record.date;
        if (!recDate && year && week && record.day !== undefined) {
          try { recDate = getDateOfDay(parseInt(year), parseInt(week), parseInt(record.day)); } catch { }
        }
        return [{
          ...record,
          user_id: baseUserId,
          employeeName: baseEmpName,
          weekNum: week || 'N/A',
          year: year || 'N/A',
          date: recDate
        }];
      });

      const finalResults = flat.filter((r) => {
        // Double check date filter on client
        if (r.date) {
          const taskDate = parseISO(r.date);
          if (!isWithinInterval(taskDate, { start: startDate, end: endDate })) return false;
        }

        // Employee filter (multi-select fallback)
        if (selectedEmployees.length > 1) {
          if (!selectedEmployees.some(e => e.id === r.user_id)) return false;
        }

        // Project filter — match against id, name string, or pathLevel1
        if (selectedProject) {
          const projName = resolveProjectName(r);
          const matchById =
            String(r.project_id) === String(selectedProject.id) ||
            String(r.pathLevel1) === String(selectedProject.id);
          const matchByName =
            projName === selectedProject.name ||
            r.pathLevel1 === selectedProject.name;
          if (!matchById && !matchByName) return false;
        }

        // Client filter — match against client_id, name, or pathLevel2
        if (selectedClient) {
          const clName = resolveClientName(r);
          const matchById = String(r.client_id) === String(selectedClient.id);
          const matchByName =
            clName === selectedClient.name ||
            r.pathLevel2 === selectedClient.name;
          if (!matchById && !matchByName) return false;
        }

        // Service filter — match against service_id, name, or pathLevel fields
        if (selectedService) {
          const svcName = resolveServiceName(r);
          const matchById = String(r.service_id) === String(selectedService.id);
          const matchByName =
            svcName === selectedService.name ||
            r.pathLevel3 === selectedService.name ||
            r.pathLevel2 === selectedService.name;
          if (!matchById && !matchByName) return false;
        }

        // Department & Office filter
        const emp = employees.find(e => e.id === r.user_id);
        if (selectedDepartment !== 'all' && emp?.department?.name !== selectedDepartment) return false;
        if (selectedOffice !== 'all' && emp?.office?.name !== selectedOffice) return false;

        return true;
      });

      setResults(finalResults);
      setSearched(true);
    } catch (err) {
      setError('Failed to fetch timesheet data.');
      console.error('[TimesheetOverview] search error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Stats ──
  const totalHours = useMemo(() => results.reduce((s, r) => s + parseFloat(r.hours || 0), 0), [results]);
  const uniqueEmps = useMemo(() => new Set(results.map((r) => r.user_id)).size, [results]);
  const uniqueProjs = useMemo(() => new Set(results.map((r) => resolveProjectName(r))).size, [results, resolveProjectName]);
  const approvedWks = useMemo(() => new Set(results.filter((r) => r.clockwise?.approved).map((r) => `${r.user_id}-${r.weekNum}-${r.year}`)).size, [results]);

  // ── Breakdown: Hours per Project ──
  const projectBreakdown = useMemo(() => {
    const map = {};
    results.forEach(r => {
      const name = resolveProjectName(r);
      map[name] = (map[name] || 0) + parseFloat(r.hours || 0);
    });
    return Object.entries(map)
      .map(([label, hours]) => ({ label, hours }))
      .sort((a, b) => b.hours - a.hours);
  }, [results, resolveProjectName]);

  // ── Breakdown: Hours per Service ──
  const serviceBreakdown = useMemo(() => {
    const map = {};
    results.forEach(r => {
      const name = resolveServiceName(r);
      map[name] = (map[name] || 0) + parseFloat(r.hours || 0);
    });
    return Object.entries(map)
      .map(([label, hours]) => ({ label, hours }))
      .sort((a, b) => b.hours - a.hours);
  }, [results, resolveServiceName]);

  // ─── PDF Export ────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4'); // portrait
      const today = new Date().toLocaleDateString('en-GB');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      // Load logos
      let marabesB64 = null;
      let axoB64 = null;
      try {
        const marabesImg = new Image();
        marabesImg.src = marabesLogo;
        await new Promise(r => { marabesImg.onload = r; marabesImg.onerror = r; });
        const c = document.createElement('canvas');
        c.width = marabesImg.naturalWidth; c.height = marabesImg.naturalHeight;
        c.getContext('2d').drawImage(marabesImg, 0, 0);
        marabesB64 = c.toDataURL('image/png');
      } catch { }
      axoB64 = await getBase64FromUrl('/cLogo.png');

      const addPageHeader = (doc) => {
        const pw = doc.internal.pageSize.getWidth();
        // Logos
        if (marabesB64) {
          try { doc.addImage(marabesB64, 'PNG', margin, 5, 30, 10); } catch { }
        }
        if (axoB64) {
          try { doc.addImage(axoB64, 'PNG', pw - margin - 30, 5, 30, 10); } catch { }
        }
        // Divider under logos
        doc.setDrawColor(63, 81, 181);
        doc.setLineWidth(0.5);
        doc.line(margin, 18, pw - margin, 18);
        // Title banner (full width)
        doc.setFillColor(232, 240, 255);
        doc.rect(margin, 20, pw - margin * 2, 10, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 58, 139);
        doc.text('Timesheet Overview Report', pw / 2, 27, { align: 'center' });
        // Sub-info bar
        doc.setFillColor(248, 249, 255);
        doc.rect(margin, 31, pw - margin * 2, 8, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90, 90, 90);
        doc.text(`Generated: ${today}`, margin + 2, 36.5);
        const periodStr = `Period: ${fmtDisplay(fromDate)}  to  ${fmtDisplay(toDate)}`;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 58, 139);
        doc.text(periodStr, pw / 2, 36.5, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90, 90, 90);
        doc.text(`${results.length} records`, pw - margin - 2, 36.5, { align: 'right' });
        // Bottom border of sub-info bar
        doc.setDrawColor(210, 220, 240);
        doc.setLineWidth(0.3);
        doc.line(margin, 39, pw - margin, 39);
      };

      addPageHeader(doc);
      let currentY = 44;

      // ── Summary section ──
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 58, 139);
      doc.text('Summary', margin, currentY);
      currentY += 4;

      const summaryData = [
        ['Total Hours', formatHours(totalHours), 'Employees', String(uniqueEmps)],
        ['Projects', String(uniqueProjs), 'Approved Weeks', String(approvedWks)],
      ];
      autoTable(doc, {
        startY: currentY,
        body: summaryData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', fillColor: [240, 244, 255], textColor: [40, 58, 139] },
          2: { fontStyle: 'bold', fillColor: [240, 244, 255], textColor: [40, 58, 139] },
        },
        margin: { left: margin, right: margin },
      });
      currentY = doc.lastAutoTable.finalY + 6;

      // ── Project Breakdown ──
      if (projectBreakdown.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 58, 139);
        doc.text('Hours by Project', margin, currentY);
        currentY += 4;
        autoTable(doc, {
          startY: currentY,
          head: [['Project', 'Hours', '% of Total']],
          body: projectBreakdown.map(r => [
            r.label,
            formatHours(r.hours),
            totalHours > 0 ? `${((r.hours / totalHours) * 100).toFixed(1)}%` : '—',
          ]),
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2.5 },
          columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
          margin: { left: margin, right: margin },
        });
        currentY = doc.lastAutoTable.finalY + 6;
      }

      // ── Service Breakdown ──
      if (serviceBreakdown.length > 0) {
        if (currentY + 30 > pageHeight - 20) { doc.addPage(); addPageHeader(doc); currentY = 40; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 58, 139);
        doc.text('Hours by Service', margin, currentY);
        currentY += 4;
        autoTable(doc, {
          startY: currentY,
          head: [['Service', 'Hours', '% of Total']],
          body: serviceBreakdown.map(r => [
            r.label,
            formatHours(r.hours),
            totalHours > 0 ? `${((r.hours / totalHours) * 100).toFixed(1)}%` : '—',
          ]),
          theme: 'striped',
          headStyles: { fillColor: [16, 150, 88], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2.5 },
          columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
          margin: { left: margin, right: margin },
        });
        currentY = doc.lastAutoTable.finalY + 6;
      }

      // ── Detail records ──
      if (results.length > 0) {
        doc.addPage();
        addPageHeader(doc);
        currentY = 40;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 58, 139);
        doc.text(`Detailed Records (${results.length})`, margin, currentY);
        currentY += 4;

        const tableBody = results.map(r => {
          const dateStr = r.date
            ? (() => { try { return format(parseISO(r.date), 'dd/MM/yyyy'); } catch { return r.date; } })()
            : DAY_NAMES[r.day] || '—';

          return [
            r.employeeName || '—',
            dateStr,
            `W${r.weekNum}/${r.year}`,
            resolveProjectName(r),
            resolveServiceName(r),
            formatHours(r.hours),
            (r.description || '—').substring(0, 50),
            r.clockwise?.approved ? 'Approved' : r.clockwise?.closed ? 'Pending' : 'Open',
          ];
        });

        autoTable(doc, {
          startY: currentY,
          head: [['Employee', 'Date', 'Week', 'Project', 'Service', 'Hours', 'Description', 'Status']],
          body: tableBody,
          theme: 'grid',
          headStyles: { fillColor: [63, 81, 181], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
          styles: { fontSize: 7, cellPadding: 1.5 },
          columnStyles: {
            1: { cellWidth: 22 }, // Date
            2: { cellWidth: 16 }, // Week
            5: { halign: 'right', fontStyle: 'bold', cellWidth: 12 }, // Hours
            7: { halign: 'center', cellWidth: 18 }, // Status
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 7) {
              const val = data.cell.raw;
              if (val === 'Approved') data.cell.styles.textColor = [22, 163, 74];
              else if (val === 'Pending') data.cell.styles.textColor = [217, 119, 6];
              else data.cell.styles.textColor = [63, 81, 181];
            }
          },
          margin: { left: margin, right: margin },
        });
      }

      // Page numbers
      const total = doc.internal.getNumberOfPages();
      for (let p = 1; p <= total; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${p} of ${total}  |  AxoPeople – Timesheet Overview`, pageWidth / 2, pageHeight - 6, { align: 'center' });
      }

      doc.save(`timesheet-overview-${fromDate}-to-${toDate}.pdf`);
    } catch (err) {
      console.error('[TimesheetOverview] PDF error:', err);
      setError('PDF export failed.');
    } finally {
      setExporting(false);
    }
  };

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { utils, writeFile } = XLSX;

      // Sheet 1: Details
      const detailRows = results.map(r => ({
        Employee: r.employeeName || '—',
        Week: `W${r.weekNum}/${r.year}`,
        Day: DAY_NAMES[r.day] ?? 'N/A',
        Project: resolveProjectName(r),
        Service: resolveServiceName(r),
        Hours: parseFloat(r.hours || 0),
        Description: r.description || '',
        Status: r.clockwise?.approved ? 'Approved' : r.clockwise?.closed ? 'Pending' : 'Open',
      }));

      // Sheet 2: Project Breakdown
      const projRows = projectBreakdown.map(r => ({
        Project: r.label,
        'Total Hours': parseFloat(r.hours.toFixed(2)),
        '% of Total': totalHours > 0 ? parseFloat(((r.hours / totalHours) * 100).toFixed(2)) : 0,
      }));

      // Sheet 3: Service Breakdown
      const svcRows = serviceBreakdown.map(r => ({
        Service: r.label,
        'Total Hours': parseFloat(r.hours.toFixed(2)),
        '% of Total': totalHours > 0 ? parseFloat(((r.hours / totalHours) * 100).toFixed(2)) : 0,
      }));

      const wb = utils.book_new();
      const ws1 = utils.json_to_sheet(detailRows);
      const ws2 = utils.json_to_sheet(projRows);
      const ws3 = utils.json_to_sheet(svcRows);

      // Column widths for details
      ws1['!cols'] = [
        { wch: 22 }, { wch: 9 }, { wch: 11 }, { wch: 28 }, { wch: 28 }, { wch: 8 }, { wch: 40 }, { wch: 10 },
      ];
      ws2['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 14 }];
      ws3['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 14 }];

      utils.book_append_sheet(wb, ws1, 'Details');
      utils.book_append_sheet(wb, ws2, 'By Project');
      utils.book_append_sheet(wb, ws3, 'By Service');

      writeFile(wb, `timesheet-overview-${fromDate}-to-${toDate}.xlsx`);
    } catch (err) {
      console.error('[TimesheetOverview] Excel error:', err);
      setError('Excel export failed.');
    } finally {
      setExporting(false);
    }
  };

  const headerActions = (
    <Stack direction="row" spacing={1}>
      {results.length > 0 && (
        <>
          <Button
            variant="outlined" size="small"
            startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <Download />}
            onClick={handleExportExcel}
            disabled={exporting}
          >
            Excel
          </Button>
          <Button
            variant="contained" size="small"
            startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdf />}
            onClick={handleExportPDF}
            disabled={exporting}
            sx={{ bgcolor: '#e53935', '&:hover': { bgcolor: '#c62828' } }}
          >
            PDF
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3, minHeight: '100vh', bgcolor: 'grey.50' }}>
      <PageHeader
        title="Timesheet Overview"
        subtitle="Search and analyse time records across the team"
        actions={headerActions}
      />

      {/* ── Filter Panel ── */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <FilterList color="primary" fontSize="small" />
          <Typography fontWeight={700} variant="body2">Filters</Typography>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          {/* Project */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={projects}
              getOptionLabel={(o) => o.name || ''}
              value={selectedProject}
              onChange={(_, v) => {
                setSelectedProject(v);
                if (v && selectedClient && selectedClient.project_id !== v.id) {
                  setSelectedClient(null);
                  setSelectedService(null);
                }
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Project" size="small" fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><FolderOpen sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Client */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={filteredClientsList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedClient}
              onChange={(_, v) => {
                setSelectedClient(v);
                if (v && !selectedProject) {
                  const p = projects.find(p => p.id === v.project_id);
                  if (p) setSelectedProject(p);
                }
                if (v && selectedService) {
                  const hasSvc = v.Services?.some(s => s.id === selectedService.id);
                  if (!hasSvc) setSelectedService(null);
                }
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><FolderOpen sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Service */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={filteredServicesList}
              getOptionLabel={(o) => o.name || o.service || ''}
              value={selectedService}
              onChange={(_, v) => {
                setSelectedService(v);
                if (v && !selectedClient) {
                  // Try to find a client that has this service if we have a project selected
                  let foundClient = null;
                  if (selectedProject) {
                    const pClients = clients.filter(c => c.project_id === selectedProject.id);
                    foundClient = pClients.find(c => c.Services?.some(s => s.id === v.id));
                  } else {
                    foundClient = clients.find(c => c.Services?.some(s => s.id === v.id));
                  }
                  if (foundClient) {
                    setSelectedClient(foundClient);
                    if (!selectedProject) {
                      const p = projects.find(p => p.id === foundClient.project_id);
                      if (p) setSelectedProject(p);
                    }
                  }
                }
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Service" size="small" fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><Assignment sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Department */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select label="Department" size="small" fullWidth
              value={selectedDepartment}
              onChange={(e) => {
                setSelectedDepartment(e.target.value);
                setSelectedEmployees([]);
              }}
            >
              {departments.map((d) => (
                <MenuItem key={d} value={d}>{d === 'all' ? 'All Departments' : d}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Office */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select label="Office" size="small" fullWidth
              value={selectedOffice}
              onChange={(e) => {
                setSelectedOffice(e.target.value);
                setSelectedEmployees([]);
              }}
            >
              {offices.map((o) => (
                <MenuItem key={o} value={o}>{o === 'all' ? 'All Offices' : o}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Employees */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple limitTags={1}
              options={filteredEmployeesList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedEmployees}
              onChange={(_, v) => setSelectedEmployees(v)}
              renderInput={(params) => (
                <TextField {...params} label="Employees" size="small" fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><Person sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* Quick Week Select */}
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              type="week" label="Quick Week" size="small" fullWidth
              value={selectedWeek}
              onChange={(e) => handleWeekChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              type="date" label="From Date" size="small" fullWidth
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setSelectedWeek(''); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              type="date" label="To Date" size="small" fullWidth
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setSelectedWeek(''); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} md={2}>
            <Button
              fullWidth variant="contained" size="medium"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ borderRadius: 2, height: 40 }}
            >
              {loading ? 'Searching…' : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Stats Row ── */}
      {searched && (
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
          <StatCard label="Total Hours" value={formatHours(totalHours)} icon={AccessTime} color={theme.palette.primary.main} />
          <StatCard label="Employees" value={uniqueEmps} icon={Person} color={theme.palette.secondary.main} />
          <StatCard label="Projects" value={uniqueProjs} icon={FolderOpen} color={theme.palette.info.main} />
          <StatCard label="Approved Weeks" value={approvedWks} icon={CheckCircle} color={theme.palette.success.main} />
        </Stack>
      )}

      {/* ── Breakdowns ── */}
      {searched && results.length > 0 && (
        <>
          {projectBreakdown.length > 0 && (
            <BreakdownSection
              title="Hours by Project"
              icon={FolderOpen}
              color={theme.palette.info.main}
              rows={projectBreakdown}
              labelKey="Project"
              totalHours={totalHours}
            />
          )}
          {serviceBreakdown.length > 0 && (
            <BreakdownSection
              title="Hours by Service"
              icon={Layers}
              color={theme.palette.success.dark}
              rows={serviceBreakdown}
              labelKey="Service"
              totalHours={totalHours}
            />
          )}
        </>
      )}

      {/* ── Results Table ── */}
      {searched && (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              px: 2.5, py: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography fontWeight={800} variant="body1" color="primary.dark">
                Timesheet Records
              </Typography>
              <Chip
                size="small"
                label={`${results.length} entries`}
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.dark', fontWeight: 700, fontSize: 11 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.5, borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <AccessTime sx={{ fontSize: 13, color: 'info.main' }} />
                <Typography variant="caption" fontWeight={700} color="info.dark">
                  {fmtDisplay(fromDate)} → {fmtDisplay(toDate)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.5, borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <AccessTime sx={{ fontSize: 13, color: 'success.main' }} />
                <Typography variant="caption" fontWeight={700} color="success.dark">
                  {formatHours(totalHours)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {results.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Search sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>No records match your filters</Typography>
              <Typography variant="body2" color="text.disabled">Try adjusting the date range or filters above</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { label: 'Employee', width: 160 },
                      { label: 'Date', width: 96 },
                      { label: 'Week', width: 70 },
                      { label: 'Project', width: 160 },
                      { label: 'Service', width: 150 },
                      { label: 'Hours', width: 70 },
                      { label: 'Description', width: 200 },
                      { label: 'Status', width: 96 },
                    ].map((col) => (
                      <TableCell
                        key={col.label}
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          color: theme.palette.primary.dark,
                          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                          whiteSpace: 'nowrap',
                          width: col.width,
                          minWidth: col.width,
                          py: 1.2,
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row, i) => {
                    const weekApproved = row.clockwise?.approved;
                    const weekClosed = row.clockwise?.closed;
                    const isEven = i % 2 === 0;
                    const initials = (row.employeeName || 'U')
                      .split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase();
                    // format date for display
                    const dateDisplay = row.date
                      ? (() => { try { return format(parseISO(row.date), 'dd MMM yyyy'); } catch { return row.date; } })()
                      : '—';

                    return (
                      <TableRow
                        key={i}
                        hover
                        sx={{
                          bgcolor: isEven ? '#fff' : alpha(theme.palette.primary.main, 0.015),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .row-employee': { color: 'primary.main' },
                          },
                          transition: 'background-color 0.15s',
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        }}
                      >
                        {/* Employee */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 28, height: 28,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 800,
                                flexShrink: 0,
                              }}
                            >
                              {initials}
                            </Box>
                            <Typography className="row-employee" variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120, transition: 'color 0.15s' }}>
                              {row.employeeName}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            {dateDisplay}
                          </Typography>
                        </TableCell>

                        {/* Week */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Box
                            sx={{
                              display: 'inline-flex', alignItems: 'center',
                              px: 0.8, py: 0.2, borderRadius: 1,
                              bgcolor: alpha(theme.palette.grey[500], 0.08),
                              fontSize: 11, fontWeight: 600, color: 'text.secondary',
                            }}
                          >
                            W{row.weekNum}/{row.year}
                          </Box>
                        </TableCell>

                        {/* Project */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <FolderOpen sx={{ fontSize: 13, color: 'info.main', flexShrink: 0 }} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140, fontWeight: 500 }}>
                              {resolveProjectName(row)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Service */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 140, color: 'text.secondary', fontSize: 12 }}>
                            {resolveServiceName(row)}
                          </Typography>
                        </TableCell>

                        {/* Hours */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Box
                            sx={{
                              display: 'inline-flex', alignItems: 'center',
                              px: 1, py: 0.3, borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.main',
                              fontWeight: 800, fontSize: 12,
                            }}
                          >
                            {formatHours(row.hours)}
                          </Box>
                        </TableCell>

                        {/* Description */}
                        <TableCell sx={{ py: 1.2 }}>
                          <Tooltip title={row.description || ''} placement="top" arrow>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ maxWidth: 190, fontSize: 12, cursor: row.description ? 'help' : 'default' }}
                            >
                              {row.description || '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.2 }}>
                          {weekApproved
                            ? <Chip size="small" label="Approved" color="success" sx={{ fontWeight: 700, fontSize: 10 }} icon={<CheckCircle sx={{ fontSize: 12 }} />} />
                            : weekClosed
                              ? <Chip size="small" label="Pending" color="warning" sx={{ fontWeight: 700, fontSize: 10 }} icon={<HourglassEmpty sx={{ fontSize: 12 }} />} />
                              : <Chip size="small" label="Open" color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: 10 }} />
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Table footer */}
          {results.length > 0 && (
            <Box
              sx={{
                px: 2.5, py: 1.2,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.grey[500], 0.03),
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" color="text.disabled">
                Showing all {results.length} records for the selected period
              </Typography>
              <Typography variant="caption" fontWeight={700} color="primary.main">
                Total: {formatHours(totalHours)}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {!searched && !loading && (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <AccessTime sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={500}>
            Set your filters and click Search
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Filter by project, service, employee, department, office, and date range
          </Typography>
        </Box>
      )}
    </Box>
  );
}
